import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toast';
import type { ServiceDTO } from '@/features/services/api';
import { useServiceMutations } from '@/features/services/hooks';
import { ApiError } from '@/lib/api';
import { formatCnpj } from '@/lib/zodSchemas/cnpj';
import {
  SERVICE_COLOR_PALETTE,
  serviceInputToApiPayload,
  serviceSchema,
  type ServiceInput,
} from '@/lib/zodSchemas/service';
import { ColorPicker } from './ColorPicker';
import { ExamTypesList } from './ExamTypesList';

interface ServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cnpjId: string;
  service: ServiceDTO | null;
}

export function ServiceForm({ open, onOpenChange, cnpjId, service }: ServiceFormProps) {
  const isEditing = !!service;
  const [tab, setTab] = useState<'general' | 'fiscal' | 'exams'>('general');
  const mutations = useServiceMutations(cnpjId);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ServiceInput>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(serviceSchema) as any,
    values: {
      name: service?.name ?? '',
      color: service?.color ?? SERVICE_COLOR_PALETTE[0],
      fixedShiftValue: service?.fixedShiftValue ?? 0,
      tomadorCnpj: service?.tomadorCnpj ? formatCnpj(service.tomadorCnpj) : '',
      tomadorRazaoSocial: service?.tomadorRazaoSocial ?? '',
      tomadorEmail: service?.tomadorEmail ?? '',
      serviceCode: service?.serviceCode ?? '',
      issRetained: service?.issRetained ?? false,
      issAliquot: service?.issAliquot ?? undefined,
      examTypes: [],
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const payload = serviceInputToApiPayload(values, cnpjId);
    try {
      if (isEditing && service) {
        const { cnpjId: _omit, ...updatePayload } = payload;
        await mutations.update.mutateAsync({ id: service.id, payload: updatePayload });
        toast.show({ title: 'Serviço atualizado', variant: 'success' });
      } else {
        await mutations.create.mutateAsync(payload);
        toast.show({ title: 'Serviço criado', variant: 'success' });
        reset();
      }
      onOpenChange(false);
    } catch (e) {
      toast.show({
        title: 'Não foi possível salvar',
        description: e instanceof ApiError ? e.message : 'Tente novamente',
        variant: 'error',
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar serviço' : 'Novo serviço'}</DialogTitle>
          <DialogDescription>
            Dados do hospital/clínica e dos exames contabilizáveis.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col" noValidate>
          <DialogBody>
            <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
              <TabsList>
                <TabsTrigger value="general">Geral</TabsTrigger>
                <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
                <TabsTrigger value="exams" disabled={!isEditing}>
                  Exames
                </TabsTrigger>
              </TabsList>

              <TabsContent value="general">
                <FormField id="svc-name" label="Nome" error={errors.name?.message} required>
                  <Input id="svc-name" invalid={!!errors.name} {...register('name')} />
                </FormField>

                <FormField
                  id="svc-fixed"
                  label="Valor fixo do plantão (R$)"
                  hint="Pode ser 0 se o serviço só paga por procedimento"
                  error={errors.fixedShiftValue?.message}
                  required
                >
                  <Input
                    id="svc-fixed"
                    inputMode="decimal"
                    invalid={!!errors.fixedShiftValue}
                    {...register('fixedShiftValue')}
                  />
                </FormField>

                <FormField id="svc-color" label="Cor" error={errors.color?.message} required>
                  <Controller
                    control={control}
                    name="color"
                    render={({ field, fieldState }) => (
                      <ColorPicker
                        value={field.value}
                        onChange={field.onChange}
                        invalid={!!fieldState.error}
                      />
                    )}
                  />
                </FormField>
              </TabsContent>

              <TabsContent value="fiscal">
                <p className="text-xs text-(--color-fg-muted)">
                  Dados usados na emissão automática de NFS-e. Podem ficar em branco
                  enquanto você não ativa a emissão para este serviço.
                </p>

                <FormField
                  id="svc-tomador-cnpj"
                  label="CNPJ do tomador"
                  error={errors.tomadorCnpj?.message}
                >
                  <Input
                    id="svc-tomador-cnpj"
                    placeholder="00.000.000/0000-00"
                    invalid={!!errors.tomadorCnpj}
                    {...register('tomadorCnpj')}
                  />
                </FormField>

                <FormField
                  id="svc-tomador-razao"
                  label="Razão social do tomador"
                  error={errors.tomadorRazaoSocial?.message}
                >
                  <Input
                    id="svc-tomador-razao"
                    invalid={!!errors.tomadorRazaoSocial}
                    {...register('tomadorRazaoSocial')}
                  />
                </FormField>

                <FormField
                  id="svc-tomador-email"
                  label="E-mail para envio da NF"
                  error={errors.tomadorEmail?.message}
                >
                  <Input
                    id="svc-tomador-email"
                    type="email"
                    invalid={!!errors.tomadorEmail}
                    {...register('tomadorEmail')}
                  />
                </FormField>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    id="svc-code"
                    label="Código de serviço municipal"
                    error={errors.serviceCode?.message}
                  >
                    <Input
                      id="svc-code"
                      invalid={!!errors.serviceCode}
                      {...register('serviceCode')}
                    />
                  </FormField>
                  <FormField
                    id="svc-iss-aliq"
                    label="Alíquota ISS (%)"
                    error={errors.issAliquot?.message}
                  >
                    <Input
                      id="svc-iss-aliq"
                      inputMode="decimal"
                      placeholder="2,50"
                      invalid={!!errors.issAliquot}
                      {...register('issAliquot')}
                    />
                  </FormField>
                </div>

                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-(--color-border)"
                    {...register('issRetained')}
                  />
                  ISS retido pelo tomador
                </label>
              </TabsContent>

              <TabsContent value="exams">
                <ExamTypesList serviceId={service?.id ?? null} />
              </TabsContent>
            </Tabs>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar serviço'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
